export const ROLE_ORDER = Object.freeze({
    Manager: 0,
    Senior: 1,
    Staff: 2,
    AdministrativeAssistant: 3,
});

export const sortMembersByRole = (members = []) =>
    [...members].sort((leftMember, rightMember) => {
        const leftRoleName = leftMember.roleName ?? leftMember.role;
        const rightRoleName = rightMember.roleName ?? rightMember.role;
        const leftRoleOrder = ROLE_ORDER[leftRoleName] ?? Number.MAX_SAFE_INTEGER;
        const rightRoleOrder =
            ROLE_ORDER[rightRoleName] ?? Number.MAX_SAFE_INTEGER;

        if (leftRoleOrder !== rightRoleOrder) {
            return leftRoleOrder - rightRoleOrder;
        }

        const leftName = `${leftMember.firstName ?? ""} ${leftMember.lastName ?? ""}`
            .trim()
            .toLowerCase();
        const rightName = `${rightMember.firstName ?? ""} ${rightMember.lastName ?? ""}`
            .trim()
            .toLowerCase();

        return leftName.localeCompare(rightName);
    });

export const getManagerCount = (members = []) =>
    members.filter((member) => member.role === "Manager").length;

export const FALLBACK_TEAM_ROLES = Object.freeze([
    { name: "Manager", isTeamLead: true, sortOrder: 0, colorHex: null },
    { name: "Senior", isTeamLead: false, sortOrder: 1, colorHex: null },
    { name: "Staff", isTeamLead: false, sortOrder: 2, colorHex: null },
    {
        name: "AdministrativeAssistant",
        isTeamLead: false,
        sortOrder: 3,
        colorHex: null,
    },
]);

const normalizeColorHex = (colorHex) => {
    if (typeof colorHex !== "string") return null;
    const normalizedColor = colorHex.trim();
    return normalizedColor || null;
};

const normalizeRoleName = (roleName) =>
    typeof roleName === "string" ? roleName.trim() : "";

export const getTeamMemberRoleName = (member) => {
    const candidates = [
        member?.teamRole?.name,
        member?.TeamRole?.Name,
        member?.teamRoleName,
        member?.TeamRoleName,
        member?.roleName,
        member?.RoleName,
        member?.role,
        member?.Role,
    ];

    return candidates.map(normalizeRoleName).find(Boolean) ?? "";
};

export const getTeamMemberRoleColorHex = (member) => {
    const candidates = [
        member?.teamRoleColorHex,
        member?.TeamRoleColorHex,
        member?.teamRole?.colorHex,
        member?.teamRole?.ColorHex,
        member?.TeamRole?.colorHex,
        member?.TeamRole?.ColorHex,
    ];

    return candidates.map(normalizeColorHex).find(Boolean) ?? null;
};

const normalizeTeamRole = (role) => ({
    ...role,
    id: role?.id ?? role?.Id,
    name: role?.name ?? role?.Name,
    colorHex: normalizeColorHex(role?.colorHex ?? role?.ColorHex),
    isDefault: role?.isDefault ?? role?.IsDefault ?? false,
    isTeamLead: role?.isTeamLead ?? role?.IsTeamLead ?? false,
    sortOrder: role?.sortOrder ?? role?.SortOrder,
});

export const sortTeamRoles = (roles = []) =>
    [...roles].sort((leftRole, rightRole) => {
        if (leftRole.isTeamLead !== rightRole.isTeamLead) {
            return leftRole.isTeamLead ? -1 : 1;
        }

        const orderDifference =
            (leftRole.sortOrder ?? Number.MAX_SAFE_INTEGER) -
            (rightRole.sortOrder ?? Number.MAX_SAFE_INTEGER);

        return orderDifference || leftRole.name.localeCompare(rightRole.name);
    });

export const normalizeTeamRoles = (roles) => {
    const validRoles = Array.isArray(roles)
        ? roles
              .map(normalizeTeamRole)
              .filter((role) => role && typeof role.name === "string")
        : [];

    return sortTeamRoles(
        validRoles.length > 0
            ? validRoles
            : FALLBACK_TEAM_ROLES.map((role) => ({ ...role })),
    );
};

export const getTeamRoleById = (teamRoleId, teamRoles = []) =>
    teamRoles.find(
        (role) =>
            String(role?.id ?? role?.Id) === String(teamRoleId),
    );

const getNestedTeamRole = (member) => member?.teamRole ?? member?.TeamRole;

const getLegacyRoleName = (member) =>
    getTeamMemberRoleName(member) ||
    normalizeRoleName(member?.employeeRole ?? member?.EmployeeRole);

export const getTeamRole = (member, teamRoles = []) => {
    const teamRoleId = member?.teamRoleId ?? member?.TeamRoleId;
    const roleById = teamRoleId
        ? getTeamRoleById(teamRoleId, teamRoles)
        : null;
    if (roleById) return roleById;

    const nestedRole = getNestedTeamRole(member);
    if (nestedRole) return normalizeTeamRole(nestedRole);

    const directColor =
        member?.teamRoleColorHex ?? member?.TeamRoleColorHex;
    const roleName = getLegacyRoleName(member);
    if (normalizeColorHex(directColor)) {
        return {
            id: teamRoleId,
            name: roleName,
            colorHex: normalizeColorHex(directColor),
        };
    }

    return teamRoles.find((role) => role.name === roleName);
};

export const getTeamRoleName = (member, teamRoles = []) =>
    getTeamRole(member, teamRoles)?.name ?? getLegacyRoleName(member);

export const getTeamRoleColor = (member, teamRoles = []) => {
    const teamRoleId = member?.teamRoleId ?? member?.TeamRoleId;
    const roleById = teamRoleId
        ? getTeamRoleById(teamRoleId, teamRoles)
        : null;
    if (roleById) return normalizeColorHex(roleById.colorHex ?? roleById.ColorHex);

    const nestedRole = getNestedTeamRole(member);
    const nestedColor = normalizeColorHex(
        nestedRole?.colorHex ?? nestedRole?.ColorHex,
    );
    if (nestedColor) return nestedColor;

    const directColor = normalizeColorHex(
        member?.teamRoleColorHex ?? member?.TeamRoleColorHex,
    );
    if (directColor) return directColor;

    const roleName = getLegacyRoleName(member);
    const roleByName = teamRoles.find((role) => role.name === roleName);
    return normalizeColorHex(roleByName?.colorHex ?? roleByName?.ColorHex);
};

export const isTeamLeadRole = (member, teamRoles = []) => {
    const matchingRole = getTeamRole(member, teamRoles);
    if (member?.teamRoleId) {
        return matchingRole?.isTeamLead === true;
    }

    const roleName = getLegacyRoleName(member);
    return matchingRole
        ? matchingRole.isTeamLead === true
        : roleName === "Manager";
};

export const getTeamLeadCount = (members = [], teamRoles = []) =>
    members.filter((member) => isTeamLeadRole(member, teamRoles)).length;

export const hasUnavailableTeamRole = (members = [], teamRoles = []) =>
    members.some(
        (member) =>
            !member.teamRoleId ||
            !teamRoles.some((role) => role.id === member.teamRoleId)
    );

export const hasDuplicateEmployee = (members = []) => {
    const employeeIds = members.map((member) => member.employeeId);
    return new Set(employeeIds).size !== employeeIds.length;
};

export const createTeamPayload = (teamState) => ({
    name: teamState.name,
    department: teamState.department,
    members: teamState.members.map((member) => ({
        employeeId: member.employeeId,
        teamRoleId: member.teamRoleId,
    })),
});
